import { Component, computed, inject, OnInit, Pipe, PipeTransform, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';

import { GAME_ID } from '../global/game-id';
import { AppEvent } from './models';
import { EventService, EventListFilter } from './event.service';
import { COUNTRY_OPTIONS, getCountryName } from '../admin/form-controls/country-select/countries';
import { UserService } from '../global/userService/user-service';
import { ImageService } from '../global/images/image.service';

@Pipe({ name: 'eventPoints', standalone: true })
export class EventPointsPipe implements PipeTransform {
    transform(event: AppEvent): string {
        const slots = (event.listSlots || []).filter((s) => s.pointsLimit != null);
        if (slots.length === 0) return '';
        if (slots.length === 1) return `${slots[0].pointsLimit}pts`;
        const values = slots.map((s) => s.pointsLimit as number);
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (min === max) return `${min}pts`;
        return `${min}–${max}pts`;
    }
}

@Component({
    standalone: false,
    selector: 'abs-event-list',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.MENU_ITEMS.EVENTS' | translate">
            <div class="buttons">
                <ion-button [routerLink]="['create']">{{ 'GLOBAL.EVENTS.CREATE_EVENT' | translate }}</ion-button>
            </div>

            <ion-segment [value]="activeTab()" (ionChange)="setTab($any($event.detail.value))">
                <ion-segment-button value="all">
                    <ion-label>{{ 'GLOBAL.EVENTS.TAB_ALL' | translate }}</ion-label>
                </ion-segment-button>
                <ion-segment-button value="mine">
                    <ion-label>{{ 'GLOBAL.EVENTS.TAB_MY_EVENTS' | translate }}</ion-label>
                </ion-segment-button>
            </ion-segment>

            @if (activeTab() === 'all') {
                <ion-card class="filters">
                    <ion-card-content>
                        <form>
                            <div class="form-row">
                                <ion-input
                                    [value]="filters.title || ''"
                                    (ionInput)="setFilter('title', $event.detail.value)"
                                    [placeholder]="'GLOBAL.EVENTS.FILTER_TITLE_PLACEHOLDER' | translate"
                                    clearInput
                                ></ion-input>
                            </div>
                            <div class="horizontal">
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_COUNTRY' | translate }}</ion-label>
                                    <ion-select
                                        interface="popover"
                                        [interfaceOptions]="{ cssClass: 'popover-wide' }"
                                        [value]="filters.countryCode || ''"
                                        (ionChange)="setFilter('countryCode', $event.detail.value)"
                                        [placeholder]="'GLOBAL.EVENTS.FILTER_COUNTRY_PLACEHOLDER' | translate"
                                    >
                                        <ion-select-option value="">{{
                                            'GLOBAL.EVENTS.FILTER_ALL_COUNTRIES' | translate
                                        }}</ion-select-option>
                                        @for (c of countryOptions; track c.value) {
                                            <ion-select-option [value]="c.value">{{ c.label }}</ion-select-option>
                                        }
                                    </ion-select>
                                </div>
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_FROM' | translate }}</ion-label>
                                    <ion-input
                                        type="date"
                                        [value]="filters.startDateFrom || ''"
                                        (ionChange)="setFilter('startDateFrom', $event.detail.value)"
                                    ></ion-input>
                                </div>
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_TO' | translate }}</ion-label>
                                    <ion-input
                                        type="date"
                                        [value]="filters.startDateTo || ''"
                                        (ionChange)="setFilter('startDateTo', $event.detail.value)"
                                    ></ion-input>
                                </div>
                            </div>
                        </form>
                    </ion-card-content>
                </ion-card>

                @if (events$ | async; as events) {
                    @if (events.length === 0) {
                        <p>{{ 'GLOBAL.EVENTS.NO_EVENTS' | translate }}</p>
                    } @else {
                        <div class="gutter-padding">
                            <div class="tile-grid">
                                @for (event of events; track event._id) {
                                    <ng-container *ngTemplateOutlet="eventCard; context: { event: event }"></ng-container>
                                }
                            </div>
                        </div>
                    }
                }
            }

            @if (activeTab() === 'mine') {
                @if (myEvents$ | async; as myEvents) {
                    @if (myEvents.length === 0) {
                        <p>{{ 'GLOBAL.EVENTS.NO_MY_EVENTS' | translate }}</p>
                    } @else {
                        <div class="gutter-padding">
                            <div class="tile-grid">
                                @for (event of myEvents; track event._id) {
                                    <ng-container *ngTemplateOutlet="eventCard; context: { event: event }"></ng-container>
                                }
                            </div>
                        </div>
                    }
                }
            }

            <ng-template #eventCard let-event="event">
                <div class="tile tile--interactive event-card" [routerLink]="[event._id]">
                    @if (event.imageId) {
                        <img [src]="imageService.url(event.imageId)" alt="" class="event-card-img" />
                    }
                    <div class="event-card-body">
                        <div class="tile-title">
                            {{ event.title }}
                            @if (event.organiserId === currentUserId()) {
                                <ion-badge color="tertiary">{{ 'GLOBAL.EVENTS.ORGANISER_BADGE' | translate }}</ion-badge>
                            }
                        </div>
                        <div class="tile-subtitle">
                            @if (event.location?.venueName) {
                                {{ event.location.venueName }},&nbsp;
                            }
                            @if (event.location?.city) {
                                {{ event.location.city }},&nbsp;
                            }
                            {{ getCountryName(event.location?.countryCode) }} &mdash; {{ event.startDate | date }}
                        </div>
                        <div class="tile-actions">
                            <ion-badge [color]="getStatusColor(event.publishStatus)">{{ event.publishStatus }}</ion-badge>
                            @let pts = event | eventPoints;
                            <span class="tile-subtitle">
                                @if (pts) {
                                    {{ pts }} &mdash;
                                }
                                {{ event.numberOfRounds }} rounds
                            </span>
                        </div>
                    </div>
                </div>
            </ng-template>
        </abs-page-layout>
    `,
    styles: [
        `
            .event-card-img {
                width: 100%;
                height: 140px;
                object-fit: cover;
                display: block;
            }

            .event-card {
                padding: 0;
            }

            .event-card-body {
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding: 10px 12px;
            }
        `
    ]
})
export class EventListPage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    gameId = inject(GAME_ID);
    imageService = inject(ImageService);

    readonly countryOptions = COUNTRY_OPTIONS;
    readonly getCountryName = getCountryName;

    activeTab = signal<'all' | 'mine'>('all');
    filters: EventListFilter = {};
    private filters$ = new BehaviorSubject<EventListFilter>({});

    events$: Observable<AppEvent[]>;
    myEvents$: Observable<AppEvent[]>;

    readonly currentUserId = computed(() => this.userService.user()?.userId);

    ngOnInit() {
        this.events$ = this.filters$.pipe(switchMap((filters) => this.eventService.listEvents(this.gameId, filters)));
        this.myEvents$ = this.userService.loggedIn$.pipe(
            switchMap((loggedIn) => (loggedIn ? this.eventService.listMyEvents(this.gameId) : of([])))
        );
    }

    setTab(tab: 'all' | 'mine') {
        this.activeTab.set(tab);
    }

    setFilter(key: keyof EventListFilter, value: string) {
        this.filters = { ...this.filters, [key]: value !== '' ? value : undefined };
        this.filters$.next(this.filters);
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'published':
                return 'success';
            case 'cancelled':
                return 'danger';
            default:
                return 'primary';
        }
    }
}
